import React, { useEffect, useRef, useState }  from 'react'
import { View, StyleSheet } from 'react-native'
import { Icon, Text, CheckBox, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import OperationsList from '../../components/OperationsList'
import HeaderIconButton from '../../components/HeaderIconButton'
import { useAtomValue } from 'jotai'
import { suppliersDictAtom, currencyFormatterAtom, storesDictAtom } from '../../global/AtomsProvider'
import { FontAwesome5 } from '@expo/vector-icons'
import { SUPPLY_ORDER_STATUSES, statusDisplayText, supplyOrderIsCompleted } from '../../utils/orderStatus'

export default function SupplyOrdersScreen({ navigation, supplierID=null, initialRange=null, showTotal=false }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [showActive, setShowActive] = useState(true)
  const operationsListRef = useRef()
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [])
  
  const renderItemFieldsToShow = (item, date) => {
    const supplier = suppliersDict[item.supplierID]
    return (
      <>
        <View style={styles.dateTimeView}>
          <Icon name='car-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {supplier?.name || t('supplies.deletedSupplier')}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='file-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {currencyFormatter.format(item.totalSum)} 
            {/* + ' (' + item.items?.reduce((total, currentItem) => total + currentItem?.quantity, 0) +  ')'} */}
          </Text>
        </View>
        
        <View style={styles.dateTimeView}>
          <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {date}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <FontAwesome5 name="building" size={16} color={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {storesDict?.[item.store]?.name}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name={(typeof item.isCompleted === 'boolean' ? item.isCompleted : supplyOrderIsCompleted(item?.status)) ? 'checkmark-circle-outline' : 'activity-outline'} width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {/* ATL-1103: `item?.status?.text` is undefined for a bare-id status, and `t(undefined)`
                is not null, so the `??` never fired and this rendered nothing at all. */}
            {statusDisplayText(item?.status, SUPPLY_ORDER_STATUSES, t) ?? t('supplyOrders.statusNew')}
          </Text>
        </View>
      </>
    )
  }

  // ATL-986: both filters are declared for BOTH read paths. Before, neither reached
  // the Supabase read path: the "active only" list included completed orders, and the
  // SupplierDetailScreen tab listed and totalled every supplier's orders.
  const activeFilter = {
    firestore: (unsubscribe) => unsubscribe.where('isCompleted', '==', false),
    api: { isCompleted: false },
  }

  const noFilter = {
    firestore: (unsubscribe) => unsubscribe,
    api: {},
  }

  const specificSupplierFilter = {
    firestore: (unsubscribe) => {
      if (supplierID != null) {
        unsubscribe = unsubscribe.where('supplierID', '==', supplierID)
      }
      return unsubscribe
    },
    api: supplierID != null ? { supplierId: supplierID } : {},
  }

  const topView = () => {
    return (
      <CheckBox style={styles.dateFilter} checked={showActive} onChange={(value) => {
        setShowActive(value)
        if (value)
          operationsListRef.current.refresh('allTime', activeFilter)
        else
          operationsListRef.current.refresh({startDate: new Date(), endDate: new Date()}, noFilter)
      }}>
        {t('supplyOrders.showActive')}
      </CheckBox>
    )
  }

  return (
    <OperationsList collectionName='supplyOrders' dateField={'date'} additionalFilters={supplierID ? specificSupplierFilter : (showActive ? activeFilter : null)} 
      detailScreenName={'SupplyOrderDetail'} topView={supplierID ? (() => {return null}) : topView} initialRange={supplierID ? initialRange : 'allTime'}
      showTotalSum={showTotal} totalSumFormatter={currencyFormatter} totalSumField={'totalSum'} ref={operationsListRef}
      showAddButton={showTotal ? false : true} additionalDataValidation={() => {}} detailScreenParams={{}}
      renderItemFieldsToShow={renderItemFieldsToShow} navigation={navigation} showStoreFilter={true}
    />
  )
}

const styles = StyleSheet.create({
  dateFilter: {
    marginHorizontal: 20,
    marginTop: 10
  },
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
});
